import React from 'react';
import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import { alignItems, boxShadow, flex } from 'styled-system';
import type { AlignItemsProps, BoxShadowProps, FlexProps } from 'styled-system';

import type { ThemeHeightProps } from 'common/theme/system-utilities';
import { themeHeight } from 'common/theme/system-utilities';
import { MessageType } from '../../enums/Alert';
import { useLocalization } from '../../providers/LocaleProvider';
import Alert from '../Alert';
import { Button, ButtonLink } from '../Button';
import { Flex } from '../Flex';
import Modal from '../Modal';
import { BodyRegularBR1 } from '../Typography';
import { getErrorMessage, GRAPHQL_ERROR_CODE } from './utils';

interface WishlistErrorModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  errorCode: string;
}

const ModalFooter = styled(Flex)<BoxShadowProps>`
  ${boxShadow};
`;

const modalFooterStyles: ComponentProps<typeof ModalFooter> = {
  py: 'sp_8',
  px: 'sp_16',
  gap: 'sp_8',
  alignItems: 'flex-start',
  boxShadow: 'high',
};

const FooterPrimaryButton = styled(Button)<FlexProps & ThemeHeightProps & AlignItemsProps>`
  ${flex};
  ${alignItems};
  ${themeHeight};
`;

const FooterSecondaryButton = styled(ButtonLink)<FlexProps & ThemeHeightProps & AlignItemsProps>`
  ${flex};
  ${alignItems};
  ${themeHeight};
`;

type FooterButtonProps =
  | ComponentProps<typeof FooterPrimaryButton>
  | ComponentProps<typeof FooterSecondaryButton>;

const footerButtonStyles: FooterButtonProps = {
  flex: 1,
  py: 'sp_16',
  px: 'sp_8',
  themeHeight: 'sp_48',
  alignItems: 'center',
};

const WishlistErrorModal = ({ isModalOpen, onClose, errorCode }: WishlistErrorModalProps) => {
  const { t } = useLocalization();

  return (
    <Modal
      isOpen={isModalOpen}
      headerTitle={
        errorCode === GRAPHQL_ERROR_CODE.WISHLIST_TOO_LONG ? t('h24_wishlist_limit_title') : ''
      }
      onClose={onClose}
    >
      {errorCode === GRAPHQL_ERROR_CODE.WISHLIST_TOO_LONG ? (
        <>
          <Flex p="sp_10" m={['sp_16', null, null, 'sp_24']}>
            <BodyRegularBR1>{t(getErrorMessage(errorCode))}</BodyRegularBR1>
          </Flex>
          <ModalFooter {...modalFooterStyles}>
            <FooterSecondaryButton
              {...footerButtonStyles}
              kind="secondary"
              href="/meinkundenkonto/meinewunschliste"
            >
              {t('h24_to_wishlist')}
            </FooterSecondaryButton>
            <FooterPrimaryButton {...footerButtonStyles} kind="primary" onClick={onClose}>
              {t('h24_got_it')}
            </FooterPrimaryButton>
          </ModalFooter>
        </>
      ) : (
        <Alert m="sp_8" type={MessageType.Error}>
          {t(getErrorMessage(errorCode))}
        </Alert>
      )}
    </Modal>
  );
};

export default WishlistErrorModal;
